import type { Account, Transaction } from '@/api/generated/model';
import { FILTER_ALL } from '@/lib/filterConstants';
import type { NetWorthHistory } from './types';

export function computeNetWorth(accounts: Account[], selectedAccount: Account | null): number {
  if (selectedAccount) {
    return selectedAccount.balance;
  }
  return accounts.reduce((sum, account) => {
    return sum + (account.type === 'liability' ? -account.balance : account.balance);
  }, 0);
}

export function buildNetWorthHistory(
  accounts: Account[],
  allTransactions: Transaction[],
  selectedAccount: Account | null
): NetWorthHistory[] {
  const currentNetWorth = computeNetWorth(accounts, selectedAccount);
  const now = new Date();
  const baselineDate = new Date(now);
  baselineDate.setFullYear(now.getFullYear() - 5);

  const filteredTransactions = selectedAccount
    ? allTransactions.filter((tx) => tx.accountId === selectedAccount.id)
    : allTransactions;

  const sortedTransactions = filteredTransactions
    .filter((tx) => tx.asOf)
    .sort((a, b) => new Date(a.asOf!).getTime() - new Date(b.asOf!).getTime());

  if (sortedTransactions.length === 0) {
    return [
      {
        date: baselineDate,
        value: currentNetWorth,
      },
      {
        date: now,
        value: currentNetWorth,
      },
    ];
  }

  const balanceBeforeFirstTransaction =
    currentNetWorth - sortedTransactions.reduce((sum, transaction) => sum + transaction.amount, 0);

  const result: NetWorthHistory[] = [
    {
      date: baselineDate,
      value: balanceBeforeFirstTransaction,
    },
  ];

  let runningBalance = balanceBeforeFirstTransaction;
  sortedTransactions.forEach((value) => {
    runningBalance += value.amount;
    result.push({
      date: new Date(value.asOf!),
      value: runningBalance,
    });
  });

  result.push({
    value: currentNetWorth,
    date: now,
  });

  return result;
}

export function filterAccountsForDisplay(
  accounts: Account[],
  selectedAccountType: string,
  selectedCategory: string
): Account[] {
  return accounts.filter((account) => {
    const matchesType = selectedAccountType === FILTER_ALL || account.type === selectedAccountType;
    const matchesCategory =
      selectedCategory === FILTER_ALL ||
      (selectedCategory === 'assets' && account.type !== 'liability') ||
      (selectedCategory === 'liabilities' && account.type === 'liability');
    return matchesType && matchesCategory;
  });
}

export function groupAccountsByType(accounts: Account[]): Record<string, Account[]> {
  return accounts.reduce(
    (groups, account) => {
      const { type } = account;
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(account);
      return groups;
    },
    {} as Record<string, Account[]>
  );
}

export function computeNetWorthTotals(accounts: Account[]) {
  const totalAssets = accounts
    .filter((acc) => acc.type !== 'liability')
    .reduce((acc, curr) => acc + curr.balance, 0);

  const totalLiabilities = accounts
    .filter((acc) => acc.type === 'liability')
    .reduce((acc, curr) => acc + curr.balance, 0);

  const netWorth = totalAssets - totalLiabilities;
  const total = totalAssets + totalLiabilities;
  const assetsPercentage = total > 0 ? (totalAssets / total) * 100 : 0;
  const liabilitiesPercentage = total > 0 ? (totalLiabilities / total) * 100 : 0;

  return { totalAssets, totalLiabilities, netWorth, assetsPercentage, liabilitiesPercentage };
}
